import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Plus, Trash2, Beaker, Sparkles, Camera, Save, Clock, Scale, Copy } from "lucide-react";
import { useBleScale } from "../lib/useBleScale";
import ScaleWidget from "./ScaleWidget";

const OZ_TO_G = 28.3495;
const gramsOf = (a, u) => (u === "oz" ? (Number(a) || 0) * OZ_TO_G : (Number(a) || 0));
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const newBowl = (n) => ({ bowl_id: `b${Date.now()}${n}`, label: `Bowl ${n}`, components: [], leftover: "", tare: "", rwUnit: "g" });

// Authenticated thumbnail for stored formula photos.
export function FormulaPhoto({ formulaId, photoId, label }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let dead = false; let obj = "";
    api.get(`/color-formulas/${formulaId}/photos/${photoId}`, { responseType: "blob" })
      .then((r) => { if (dead) return; obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { dead = true; if (obj) URL.revokeObjectURL(obj); };
  }, [formulaId, photoId]);
  return (
    <div className="relative">
      {url ? <img src={url} alt={label} className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
        : <div className="w-14 h-14 rounded-lg bg-slate-100 animate-pulse" />}
      <span className="absolute bottom-0 inset-x-0 text-[8px] text-center text-white bg-black/40 rounded-b-lg capitalize">{label}</span>
    </div>
  );
}

export default function FormulaPanel({ clientId, clientName, appointmentId }) {
  const [tab, setTab] = useState("history");
  const [products, setProducts] = useState([]);
  const [multiplier, setMultiplier] = useState(2.75);
  const [history, setHistory] = useState([]);
  const [bowls, setBowls] = useState([newBowl(1)]);
  const [developer, setDeveloper] = useState("");
  const [minutes, setMinutes] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [wasteFor, setWasteFor] = useState(null);     // formula_id being reweighed
  const [wasteInputs, setWasteInputs] = useState({});  // { bowl_id: {amount, unit} }
  const [wasteSaving, setWasteSaving] = useState(false);

  const scale = useBleScale();
  const captureComp = (bi, ci) => {
    if (!scale.connected) { toast.error("Connect the Bluetooth scale first"); return; }
    updateComponent(bi, ci, { amount: String(scale.weight), unit: "g" });
  };

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.product_id, p])), [products]);

  const loadHistory = useCallback(async () => {
    if (!clientId) return;
    try { const r = await api.get(`/color-formulas?client_id=${clientId}`); setHistory(r.data || []); } catch { /* */ }
  }, [clientId]);

  useEffect(() => {
    api.get("/color-products?active_only=true").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/color-bar/settings").then((r) => setMultiplier(r.data?.charge_multiplier || 2.75)).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { setTab(history.length ? "history" : "new"); }, [history.length]);

  const compCost = (c) => gramsOf(c.amount, c.unit) * (productMap[c.product_id]?.cost_per_gram || 0);
  const bowlCost = (b) => b.components.reduce((s, c) => s + compCost(c), 0);
  const totalCost = bowls.reduce((s, b) => s + bowlCost(b), 0);
  const suggested = totalCost * multiplier;
  const hasComp = bowls.some((b) => b.components.length > 0);

  const addComponent = (i) => setBowls((b) => b.map((bw, idx) => idx === i ? { ...bw, components: [...bw.components, { product_id: "", amount: "", unit: "g" }] } : bw));
  const updateComponent = (bi, ci, patch) => setBowls((b) => b.map((bw, idx) => idx === bi ? { ...bw, components: bw.components.map((c, cx) => cx === ci ? { ...c, ...patch } : c) } : bw));
  const removeComponent = (bi, ci) => setBowls((b) => b.map((bw, idx) => idx === bi ? { ...bw, components: bw.components.filter((_, cx) => cx !== ci) } : bw));

  const resetForm = () => { setBowls([newBowl(1)]); setDeveloper(""); setMinutes(""); setNotes(""); };

  const duplicateLast = () => {
    const last = history[0];
    if (!last) return;
    setBowls((last.bowls || []).map((b, i) => ({
      bowl_id: `b${Date.now()}${i}`, label: b.label || `Bowl ${i + 1}`,
      components: (b.components || []).map((c) => ({ product_id: c.product_id || "", amount: String(c.amount ?? ""), unit: c.unit || "g" })),
      leftover: "", tare: "", rwUnit: "g",
    })) || [newBowl(1)]);
    setDeveloper(last.developer || "");
    setMinutes(last.processing_minutes ? String(last.processing_minutes) : "");
    setNotes(last.notes || "");
    setTab("new");
    toast.success("Copied last formula — tweak and save");
  };

  const save = async () => {
    if (!hasComp) { toast.error("Add at least one product"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId, client_name: clientName, appointment_id: appointmentId || null,
        developer, processing_minutes: minutes ? Number(minutes) : null, notes,
        bowls: bowls.filter((b) => b.components.length).map((b) => ({
          bowl_id: b.bowl_id, label: b.label,
          components: b.components.filter((c) => c.product_id).map((c) => ({ product_id: c.product_id, amount: Number(c.amount) || 0, unit: c.unit })),
          waste_amount: Number(b.leftover) || 0, waste_unit: b.rwUnit || "g", tare: Number(b.tare) || 0,
        })),
      };
      const res = await api.post("/color-formulas", payload);
      setLastSaved(res.data);
      toast.success(`Formula saved · suggested ${money(res.data.suggested_charge)}`);
      resetForm();
      await loadHistory();
      setTab("history");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save formula"); }
    setSaving(false);
  };

  const attachPhoto = async (formulaId, label, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("label", label); fd.append("file", file);
    try {
      await api.post(`/color-formulas/${formulaId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} photo added`); await loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const mixedGramsOfBowl = (b) => (b.components || []).reduce((s, c) => s + gramsOf(c.amount, c.unit), 0);
  const openReweigh = (f) => {
    setWasteFor(f.formula_id);
    const init = {};
    (f.bowls || []).forEach((b) => { init[b.bowl_id] = { amount: b.waste_amount ? String(b.waste_amount) : "", unit: b.waste_unit || "g", tare: b.tare ? String(b.tare) : "" }; });
    setWasteInputs(init);
  };
  const saveWaste = async (f) => {
    setWasteSaving(true);
    try {
      const bowls = (f.bowls || []).map((b) => ({
        bowl_id: b.bowl_id,
        waste_amount: Number(wasteInputs[b.bowl_id]?.amount) || 0,
        waste_unit: wasteInputs[b.bowl_id]?.unit || "g",
        tare: Number(wasteInputs[b.bowl_id]?.tare) || 0,
      }));
      const res = await api.post(`/color-formulas/${f.formula_id}/waste`, { bowls });
      toast.success(`Waste logged · ${money(res.data.total_waste_cost)} wasted, ${money(res.data.used_cost)} used`);
      setWasteFor(null); await loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save waste"); }
    setWasteSaving(false);
  };

  const inp = "h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-900 font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-fuchsia-300";

  return (
    <div data-testid="formula-panel">
      <div className="flex gap-2 mb-4">
        {[["history", `History${history.length ? ` (${history.length})` : ""}`], ["new", "Log new formula"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`formula-tab-${k}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold font-['Manrope'] border ${tab === k ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "history" && (
        <div className="space-y-3" data-testid="formula-history">
          {history.length === 0 && <p className="text-sm text-slate-400 font-['Manrope'] py-6 text-center">No formulas yet for {clientName || "this client"}. Tap "Log new formula".</p>}
          {history.map((f) => (
            <div key={f.formula_id} className="border border-slate-100 rounded-xl p-3" data-testid={`formula-entry-${f.formula_id}`}>
              <div className="flex justify-between items-start">
                <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{f.service_date} · {f.stylist_name}</p>
                <span className="text-xs text-slate-500 font-['Manrope'] text-right">
                  {money(f.total_cost)} cost · {money(f.suggested_charge)} charge
                  {f.total_waste_cost != null && (
                    <span className="block text-amber-600">{money(f.total_waste_cost)} waste · {money(f.used_cost)} used</span>
                  )}
                </span>
              </div>
              {(f.bowls || []).map((b) => (
                <p key={b.bowl_id} className="text-xs text-slate-600 font-['Manrope'] mt-1 flex items-start gap-1">
                  <Beaker className="w-3.5 h-3.5 text-fuchsia-400 mt-0.5 shrink-0" />
                  <span><span className="font-semibold">{b.label}:</span> {(b.components || []).map((c) => `${c.product_name} ${c.amount}${c.unit}`).join(" + ")}</span>
                </p>
              ))}
              {(f.developer || f.processing_minutes) && <p className="text-xs text-slate-400 font-['Manrope'] mt-1">{f.developer}{f.developer && f.processing_minutes ? " · " : ""}{f.processing_minutes ? `${f.processing_minutes} min` : ""}</p>}
              {f.notes && <p className="text-xs text-slate-400 italic font-['Manrope'] mt-1">"{f.notes}"</p>}
              <div className="flex items-center gap-2 mt-2">
                {(f.photos || []).map((p) => <FormulaPhoto key={p.photo_id} formulaId={f.formula_id} photoId={p.photo_id} label={p.label} />)}
                {["before", "after"].map((lbl) => (
                  <label key={lbl} data-testid={`formula-add-photo-${f.formula_id}-${lbl}`}
                    className="cursor-pointer inline-flex items-center gap-1 h-8 px-3 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-500 text-xs font-['Manrope']">
                    <Camera className="w-3.5 h-3.5" /><span className="capitalize">{lbl}</span>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const fl = e.target.files?.[0]; e.target.value = ""; attachPhoto(f.formula_id, lbl, fl); }} />
                  </label>
                ))}
                <button onClick={() => (wasteFor === f.formula_id ? setWasteFor(null) : openReweigh(f))}
                  data-testid={`formula-reweigh-btn-${f.formula_id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-xs font-['Manrope'] font-semibold">
                  <Scale className="w-3.5 h-3.5" /> Reweigh / waste
                </button>
              </div>

              {/* Reweigh: enter leftover per bowl → proportional waste + used cost */}
              {wasteFor === f.formula_id && (
                <div className="mt-3 p-3 rounded-xl bg-amber-50/60 border border-amber-200 space-y-2" data-testid={`formula-reweigh-${f.formula_id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 font-['Manrope']">Reweigh leftover in each bowl <span className="text-amber-500 normal-case tracking-normal">(tare = empty bowl weight)</span></p>
                  <ScaleWidget scale={scale} variant="light" testid={`formula-reweigh-scale-${f.formula_id}`} />
                  {(f.bowls || []).map((b) => {
                    const mixed = mixedGramsOfBowl(b);
                    const wIn = wasteInputs[b.bowl_id] || { amount: "", unit: "g", tare: "" };
                    const netG = Math.max(0, gramsOf(wIn.amount, wIn.unit) - gramsOf(wIn.tare, wIn.unit));
                    const wG = Math.min(netG, mixed);
                    const wCost = mixed > 0 ? (wG / mixed) * (b.cost || 0) : 0;
                    return (
                      <div key={b.bowl_id} className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-600 font-['Manrope'] flex-1 truncate min-w-[80px]">{b.label} <span className="text-slate-400">({mixed.toFixed(0)}g mixed)</span></span>
                        <input type="number" min="0" value={wIn.tare} placeholder="tare" title="Empty bowl weight" data-testid={`formula-waste-tare-${b.bowl_id}`}
                          onChange={(e) => setWasteInputs((s) => ({ ...s, [b.bowl_id]: { ...wIn, tare: e.target.value } }))}
                          className="w-20 h-9 rounded-lg border border-amber-200 px-2 text-sm text-center font-['Manrope'] focus:outline-none" />
                        <input type="number" min="0" value={wIn.amount} placeholder="leftover" data-testid={`formula-waste-amount-${b.bowl_id}`}
                          onChange={(e) => setWasteInputs((s) => ({ ...s, [b.bowl_id]: { ...wIn, amount: e.target.value } }))}
                          className="w-20 h-9 rounded-lg border border-amber-200 px-2 text-sm text-center font-['Manrope'] focus:outline-none" />
                        <div className="flex rounded-lg overflow-hidden border border-amber-200">
                          {["g", "oz"].map((u) => <button key={u} onClick={() => setWasteInputs((s) => ({ ...s, [b.bowl_id]: { ...wIn, unit: u } }))} className={`w-8 h-9 text-xs font-semibold ${wIn.unit === u ? "bg-amber-500 text-white" : "bg-white text-slate-500"}`}>{u}</button>)}
                        </div>
                        {scale.connected && (
                          <>
                            <button onClick={() => setWasteInputs((s) => ({ ...s, [b.bowl_id]: { ...wIn, tare: String(scale.weight), unit: "g" } }))} data-testid={`formula-waste-capture-tare-${b.bowl_id}`}
                              className="h-9 px-2.5 rounded-lg bg-amber-500 text-white text-xs font-semibold font-['Manrope'] hover:bg-amber-600 flex items-center gap-1"><Scale className="w-3.5 h-3.5" />Tare</button>
                            <button onClick={() => setWasteInputs((s) => ({ ...s, [b.bowl_id]: { ...wIn, amount: String(scale.weight), unit: "g" } }))} data-testid={`formula-waste-capture-leftover-${b.bowl_id}`}
                              className="h-9 px-2.5 rounded-lg bg-sky-600 text-white text-xs font-semibold font-['Manrope'] hover:bg-sky-700 flex items-center gap-1"><Scale className="w-3.5 h-3.5" />Reweigh</button>
                          </>
                        )}
                        <span className="text-xs text-amber-700 font-['Manrope'] tabular-nums w-16 text-right">{money(wCost)} waste</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button onClick={() => setWasteFor(null)} variant="outline" className="h-8 rounded-full text-xs" data-testid={`formula-waste-cancel-${f.formula_id}`}>Cancel</Button>
                    <Button onClick={() => saveWaste(f)} disabled={wasteSaving} className="h-8 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid={`formula-waste-save-${f.formula_id}`}>{wasteSaving ? "Saving…" : "Save waste"}</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "new" && (
        <div className="space-y-4" data-testid="formula-new">
          <ScaleWidget scale={scale} variant="light" testid="formula-scale" />
          {history.length > 0 && (
            <button onClick={duplicateLast} data-testid="formula-duplicate-last"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 text-sm font-semibold font-['Manrope'] hover:bg-fuchsia-100">
              <Copy className="w-4 h-4" /> Duplicate last formula ({history[0].service_date})
            </button>
          )}
          <div className="space-y-3">
            {bowls.map((b, bi) => (
              <div key={b.bowl_id} className="border border-slate-200 rounded-xl p-3" data-testid={`formula-bowl-${bi}`}>
                <div className="flex items-center justify-between mb-2">
                  <input value={b.label} onChange={(e) => setBowls((arr) => arr.map((x, i) => i === bi ? { ...x, label: e.target.value } : x))}
                    className="bg-transparent text-slate-900 font-['Outfit'] font-semibold text-sm focus:outline-none border-b border-transparent focus:border-slate-200" data-testid={`formula-bowl-label-${bi}`} />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 tabular-nums">{money(bowlCost(b))}</span>
                    {bowls.length > 1 && <button onClick={() => setBowls((arr) => arr.filter((_, i) => i !== bi))} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                <div className="space-y-2">
                  {b.components.map((c, ci) => (
                    <div key={ci} className="flex items-center gap-2" data-testid={`formula-comp-${bi}-${ci}`}>
                      <select value={c.product_id} onChange={(e) => updateComponent(bi, ci, { product_id: e.target.value })} className={`${inp} flex-1`} data-testid={`formula-comp-product-${bi}-${ci}`}>
                        <option value="">Select product…</option>
                        {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.brand ? `${p.brand} ` : ""}{p.name}{p.shade ? ` (${p.shade})` : ""} · {p.type}</option>)}
                      </select>
                      <input type="number" min="0" value={c.amount} onChange={(e) => updateComponent(bi, ci, { amount: e.target.value })} placeholder="0" className={`${inp} w-20 text-center`} data-testid={`formula-comp-amount-${bi}-${ci}`} />
                      <div className="flex rounded-lg overflow-hidden border border-slate-200">
                        {["g", "oz"].map((u) => <button key={u} onClick={() => updateComponent(bi, ci, { unit: u })} className={`w-8 h-10 text-xs font-semibold ${c.unit === u ? "bg-fuchsia-600 text-white" : "bg-white text-slate-500"}`}>{u}</button>)}
                      </div>
                      <button onClick={() => captureComp(bi, ci)} title={scale.connected ? "Weigh with scale" : "Connect scale above"} data-testid={`formula-weigh-${bi}-${ci}`}
                        className={`w-9 h-10 rounded-lg border flex items-center justify-center ${scale.connected ? "bg-sky-50 border-sky-200 text-sky-600 hover:bg-sky-100" : "bg-white border-slate-200 text-slate-300"}`}><Scale className="w-4 h-4" /></button>
                      <button onClick={() => removeComponent(bi, ci)} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => addComponent(bi)} className="flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-['Manrope']" data-testid={`formula-add-comp-${bi}`}><Plus className="w-4 h-4" /> Add product</button>
                </div>
                {/* Optional reweigh before closing: leftover − tare = waste */}
                {(() => {
                  const mixed = b.components.reduce((s, c) => s + gramsOf(c.amount, c.unit), 0);
                  const netG = Math.max(0, gramsOf(b.leftover, b.rwUnit) - gramsOf(b.tare, b.rwUnit));
                  const frac = mixed > 0 ? Math.min(netG, mixed) / mixed : 0;
                  const wCost = bowlCost(b) * frac;
                  const setBowl = (patch) => setBowls((arr) => arr.map((x, i) => i === bi ? { ...x, ...patch } : x));
                  return (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap" data-testid={`formula-reweigh-inline-${bi}`}>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 font-['Manrope']">Reweigh</span>
                      <input type="number" min="0" value={b.tare} onChange={(e) => setBowl({ tare: e.target.value })} placeholder="bowl tare" title="Empty bowl weight"
                        data-testid={`formula-tare-${bi}`} className="w-24 h-9 rounded-lg border border-amber-200 px-2 text-sm text-center font-['Manrope'] focus:outline-none" />
                      <input type="number" min="0" value={b.leftover} onChange={(e) => setBowl({ leftover: e.target.value })} placeholder="leftover" title="Scale reading of leftover"
                        data-testid={`formula-leftover-${bi}`} className="w-24 h-9 rounded-lg border border-amber-200 px-2 text-sm text-center font-['Manrope'] focus:outline-none" />
                      <div className="flex rounded-lg overflow-hidden border border-amber-200">
                        {["g", "oz"].map((u) => <button key={u} onClick={() => setBowl({ rwUnit: u })} className={`w-8 h-9 text-xs font-semibold ${b.rwUnit === u ? "bg-amber-500 text-white" : "bg-white text-slate-500"}`}>{u}</button>)}
                      </div>
                      {scale.connected && (
                        <>
                          <button onClick={() => setBowl({ tare: String(scale.weight), rwUnit: "g" })} data-testid={`formula-capture-tare-${bi}`}
                            className="h-9 px-2.5 rounded-lg bg-amber-500 text-white text-xs font-semibold font-['Manrope'] hover:bg-amber-600 flex items-center gap-1"><Scale className="w-3.5 h-3.5" />Tare</button>
                          <button onClick={() => setBowl({ leftover: String(scale.weight), rwUnit: "g" })} data-testid={`formula-capture-leftover-${bi}`}
                            className="h-9 px-2.5 rounded-lg bg-sky-600 text-white text-xs font-semibold font-['Manrope'] hover:bg-sky-700 flex items-center gap-1"><Scale className="w-3.5 h-3.5" />Reweigh</button>
                        </>
                      )}
                      {b.leftover !== "" && <span className="text-xs text-amber-700 font-['Manrope'] tabular-nums">{money(wCost)} waste</span>}
                    </div>
                  );
                })()}
              </div>
            ))}
            <button onClick={() => setBowls((b) => [...b, newBowl(b.length + 1)])} className="w-full py-2.5 rounded-lg border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 text-sm font-['Manrope'] font-semibold flex items-center justify-center gap-2" data-testid="formula-add-bowl"><Plus className="w-4 h-4" /> Add another bowl</button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Developer</p>
              <input value={developer} onChange={(e) => setDeveloper(e.target.value)} placeholder="20 vol" className={`${inp} w-full`} data-testid="formula-developer" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Processing (min)</p>
              <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-slate-400" /><input type="number" min="0" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="35" className={`${inp} w-full`} data-testid="formula-minutes" /></div>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Notes</p>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 2000))} placeholder="Root touch-up, pulled through last 10 min…" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-fuchsia-300" data-testid="formula-notes" />
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-fuchsia-50 to-violet-50 border border-fuchsia-200">
            <div>
              <p className="text-xs text-slate-500 font-['Manrope']">Product cost <span className="font-semibold text-slate-800 tabular-nums" data-testid="formula-total-cost">{money(totalCost)}</span></p>
              <p className="text-sm font-semibold text-fuchsia-900 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Suggested charge <span className="tabular-nums" data-testid="formula-suggested">{money(suggested)}</span></p>
            </div>
            <Button onClick={save} disabled={saving || !hasComp} className="h-11 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="formula-save">
              <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save formula"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
